import { MenuProps, Table } from 'antd';
import ActionsMenu from './ui/ActionsMenu';
import React, { useState, useEffect } from 'react';
import { AnyObject } from 'antd/es/_util/type';
import { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, KeyOutlined } from '@ant-design/icons';
import DeleteModal from './ui/modal/DeleteModal';
import ChangePasswordModal from './ChangePasswordModal';

interface BaseTableProps<T> {
  dataSource: T[];
  isLoading: boolean;
  handleEditButtonClick?: (record: T) => void;
  handleDeleteButtonClick?: (record: T) => void;
  module: string;
  columns: ColumnsType<T>;
  userRole?: string | undefined;
  canEdit?: boolean | undefined;
  canDelete?: boolean | undefined;
  canChangePassword?: boolean | undefined;
}

const BaseTable = <T extends AnyObject>({
  dataSource,
  isLoading = true,
  handleEditButtonClick,
  handleDeleteButtonClick,
  module,
  columns: initialColumns,
  userRole,
  canEdit,
  canDelete,
  canChangePassword = false,
}: BaseTableProps<T>) => {
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<T | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [columns, setColumns] = useState<ColumnsType<T>>(initialColumns);
  const [userId, setUserId] = useState('');
  useEffect(() => {
    // Add action column if the user has the admin role or canEdit or canDelete
    if (userRole === 'ROLE_ADMIN' || canEdit || canDelete) {
      setColumns((prevColumns) => {
        const hasActionColumn = prevColumns.some(
          (column) => column.key === 'action',
        );
        if (!hasActionColumn) {
          return [
            ...prevColumns,
            {
              key: 'action',
              title: 'Action',
              dataIndex: 'actions',
              width: 150,
              render: (_text, record) => {
                // Same Actions control the detail pages use, so a row and a
                // record screen offer their verbs the same way. Entries are
                // built from the caller's permissions, so a table with only
                // edit rights shows only Edit.
                const rowItems: MenuProps['items'] = [
                  ...(canEdit
                    ? [{ key: 'edit', label: 'Edit', icon: <EditOutlined /> }]
                    : []),
                  ...(canChangePassword
                    ? [
                        {
                          key: 'password',
                          label: 'Change Password',
                          icon: <KeyOutlined />,
                        },
                      ]
                    : []),
                  ...(canDelete
                    ? [
                        {
                          key: 'delete',
                          label: 'Delete',
                          icon: <DeleteOutlined />,
                          danger: true,
                        },
                      ]
                    : []),
                ];

                const onRowAction: MenuProps['onClick'] = ({ key }) => {
                  if (key === 'edit' && handleEditButtonClick)
                    handleEditButtonClick(record);
                  else if (key === 'password') handleChangePassword(record);
                  else if (key === 'delete')
                    handleDeleteButtonClickInternal(record);
                };

                return <ActionsMenu items={rowItems} onClick={onRowAction} />;
              },
            },
          ];
        }
        return prevColumns;
      });
    }
  }, []);

  const handleDeleteButtonClickInternal = (record: T) => {
    setRecordToDelete(record);
    setDeleteModalOpen(true);
  };

  const handleChangePassword = (record: T) => {
    setUserId(record.id);
    setIsModalVisible(true);
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
  };

  const handleDeleteModalConfirm = () => {
    if (recordToDelete) {
      if (handleDeleteButtonClick) {
        handleDeleteButtonClick(recordToDelete);
      }
      setDeleteModalOpen(false);
      setRecordToDelete(null);
    }
  };

  const handleCloseChangePasswordModal = () => {
    setIsModalVisible(false);
  };

  return (
    <>
      <div className={'flex flex-col h-[calc(79vh)] overflow-auto'}>
        <Table<T>
          rowKey={(record) => record.id}
          // scroll={{ y: 580 }}
          dataSource={dataSource}
          loading={isLoading}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          columns={columns}
        />
      </div>
      <DeleteModal
        message={
          'This action cannot be undone and all associated data will be permanently removed from our system.'
        }
        onClose={handleDeleteModalClose}
        visible={isDeleteModalOpen}
        title={`Delete ${module}?`}
        onConfirm={handleDeleteModalConfirm}
      />

      {userId !== '' && (
        <ChangePasswordModal
          userId={userId}
          visible={isModalVisible}
          onClose={handleCloseChangePasswordModal}
        />
      )}
    </>
  );
};

export default BaseTable;
